import { useEffect, useSyncExternalStore } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocale } from '../contexts/LocaleContext';
import { hardwareFirstUnitCount } from '../lib/hardwareFirst';
import { useHardwareFirstGiftingOfferedForCart } from '../lib/hardwareFirstGifting';
import { latestCartPricing, serverCartPricing, subscribeToCartPricing } from '../lib/pricing';
import { cartActions } from '../reducers/storeCart';
import * as types from '../types';

/**
 * Keeps the store cart priced in the locale's currency. The cart's currency is set when an
 * item is added and persists in localStorage, so a shopper who switches country afterwards
 * would otherwise check out a USD cart against a CA account and be rejected at purchase.
 * Hardcoded to the store cart: it runs at the app root, outside any route-driven cart mode.
 */
export default function useStoreCartCurrencySync(): void {
  const { currency } = useLocale();
  const cart = useSelector((state: types.AppState) => state.storeShop.cart);
  const products = useSelector((state: types.AppState) => state.config.products);
  // before any pricing arrives gifting is unknown, not off, so a switch keeps the gift the stash held
  const pricingKnown = useSyncExternalStore(subscribeToCartPricing, latestCartPricing, serverCartPricing) !== undefined;
  const offered = useHardwareFirstGiftingOfferedForCart(cart) || !pricingKnown;
  const dispatch = useDispatch();

  useEffect(() => {
    if (cart.currency !== currency) {
      dispatch(cartActions.setCurrency({ currency, unitsInCart: hardwareFirstUnitCount(cart, products), offered }));
    }
  }, [cart, currency, dispatch, offered, products]);
}
